import api, { API_BASE_URL } from '../lib/api';
import type {
  LoginCredentials,
  AuthResponse,
  ForgotPasswordData,
  ResetPasswordData,
  User,
} from '../types';

export const authService = {
  // Login
  login: async (credentials: LoginCredentials): Promise<{ user: User; token: string }> => {
    const response = await api.post<AuthResponse>('/login', {
      user: credentials,
    });

    // Extract JWT from Authorization header (check both cases for compatibility)
    const authHeader = response.headers['authorization'] || response.headers['Authorization'];
    const token = authHeader?.replace('Bearer ', '');

    if (!token) {
      console.error('No authorization token in response headers:', response.headers);
      throw new Error('No authorization token received');
    }

    // Store token and user data
    localStorage.setItem('auth_token', token);
    localStorage.setItem('user', JSON.stringify(response.data.user));

    console.log('Login successful - stored in localStorage:', {
      hasToken: !!token,
      tokenLength: token.length,
      hasUser: !!response.data.user,
      userEmail: response.data.user.email
    });

    return {
      user: response.data.user,
      token,
    };
  },

  // Microsoft (Entra ID) sign-in, ENG-746. The browser goes to the API, which
  // sends it to Microsoft and back to /auth/callback?code=...
  microsoftSignInUrl: (): string => `${API_BASE_URL}/auth/entra_id`,

  // Swap the code from /auth/callback for the same token and user as login.
  exchangeMicrosoftCode: async (code: string): Promise<{ user: User; token: string }> => {
    const response = await api.post<AuthResponse>('/auth/exchange', { code });

    const authHeader = response.headers['authorization'] || response.headers['Authorization'];
    const token = authHeader?.replace('Bearer ', '');
    if (!token) {
      throw new Error('No authorization token received');
    }

    localStorage.setItem('auth_token', token);
    localStorage.setItem('user', JSON.stringify(response.data.user));

    return {
      user: response.data.user,
      token,
    };
  },

  // Logout
  logout: async (): Promise<void> => {
    try {
      await api.delete('/logout');
    } finally {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
    }
  },

  // Forgot password
  forgotPassword: async (data: ForgotPasswordData): Promise<{ message: string }> => {
    const response = await api.post('/password', {
      user: data,
    });
    return response.data;
  },

  // Reset password
  resetPassword: async (data: ResetPasswordData): Promise<{ message: string }> => {
    const response = await api.put('/password', {
      user: data,
    });
    return response.data;
  },

  // Get current user from localStorage
  getCurrentUser: (): User | null => {
    const userStr = localStorage.getItem('user');
    if (!userStr) {
      console.log('No user data found in localStorage');
      return null;
    }
    try {
      const user = JSON.parse(userStr) as User;
      console.log('User data loaded from localStorage:', { id: user.id, email: user.email });
      return user;
    } catch (error) {
      console.error('Failed to parse user data from localStorage:', error);
      return null;
    }
  },

  // Get auth token from localStorage
  getToken: (): string | null => {
    return localStorage.getItem('auth_token');
  },

  // Check if user is authenticated
  isAuthenticated: (): boolean => {
    return !!authService.getToken();
  },
};

export default authService;
